import React, {Component} from 'react';
import axios from 'axios';
import Drawer from '@material-ui/core/Drawer';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import Auth from '../../../auth/auth';
import Button from '../../../components/button';
import AccessRefusal from '../../../components/access-refusal';

const API = process.env.REACT_APP_API_URL || '';

// 30, deliberately NOT 90. The model clamps to `maxDelegationDays()`, which defaults to 90 measured
// from `Date.now()` — so a +90-day default submitted as `T23:59:59` lands ~14 hours PAST the cap and
// is refused every time. The default value of a form must not be one the server always rejects,
// which is the same "a form that can only be rejected is not a form" argument that makes the field
// required in the first place. A shorter default also reads better for a confidential-sight share;
// anyone who wants longer can pick a date, and rule 6's refusal names the cap when they overshoot.
const DEFAULT_EXPIRY_DAYS = 30;

function authHeaders() {
  return {'Accept': 'application/json', 'Content-Type': 'application/json', 'x-access-token': new Auth().getAccessToken()};
}

/**
 * `WhoCanSeePanel`'s "+ Share sight with a team" drawer — Q5 (docs/access/q5-q7-decisions.md),
 * built per docs/access/slice-4d-4e-plan.md §3.6.
 *
 * Class + local state, matching `DelegateDrawer` from 4c — no hooks.
 *
 * EXPIRY IS REQUIRED, and defaulted well inside the cap — see `DEFAULT_EXPIRY_DAYS` below for why
 * the number is not 90. The model's rule 6 refuses an open-ended share, and rule 7 refuses one that
 * outlives the sharer's own sight; a form whose default value the server rejects is not a form.
 *
 * Every refusal renders through `<AccessRefusal>` so the steward is told WHICH rule refused and
 * why, not "Forbidden" — `assertShareIsPermitted`'s seven rules each carry their own message.
 */
class ShareSightDrawer extends Component {
  constructor(props) {
    super(props);
    this.state = this.emptyForm();
  }

  emptyForm() {
    return {targets: [], subject: null, expiresAt: this.defaultExpiry(), error: null, loading: false};
  }

  defaultExpiry() {
    const d = new Date();
    d.setDate(d.getDate() + DEFAULT_EXPIRY_DAYS);
    return d.toISOString().slice(0, 10);
  }

  // The drawer is kept mounted and toggled by `open`, so without this a second share opens holding
  // the first one's target — the same class of defect `DelegateDrawer`'s guard exists for.
  componentDidUpdate(prevProps) {
    if (!prevProps.open && this.props.open) {
      this.setState(this.emptyForm(), () => this.loadTargets());
    }
  }

  loadTargets() {
    const {scopeType, scopeId} = this.props;
    if (!scopeType || !scopeId) return;
    axios.get(`${API}/access/share-targets`, {
      params: {scopeType, scopeId},
      headers: authHeaders(),
    })
        .then((res) => this.setState({targets: (res.data && res.data.targets) || []}))
        .catch((err) => this.setState({error: this.errorFrom(err)}));
  }

  errorFrom(err) {
    return (err.response && err.response.data) || {error: String(err.message || err)};
  }

  handleSubmit = () => {
    const {scopeType, scopeId} = this.props;
    const {subject, expiresAt} = this.state;
    if (!subject || !expiresAt) return;

    this.setState({loading: true, error: null});
    axios.post(`${API}/access/shares`, {
      subjectId: subject.subjectId,
      scopeType,
      scopeId,
      expiresAt: `${expiresAt}T23:59:59`,
    }, {headers: authHeaders()})
        .then(() => {
          this.setState({loading: false});
          if (this.props.onSuccess) this.props.onSuccess();
          this.props.onClose();
        })
        .catch((err) => this.setState({loading: false, error: this.errorFrom(err)}));
  };

  render() {
    const {open, onClose} = this.props;
    const {targets, subject, expiresAt, error, loading} = this.state;
    const canSubmit = Boolean(subject) && Boolean(expiresAt) && !loading;

    return (
      <Drawer anchor="right" open={open} onClose={onClose}
        PaperProps={{'className': 'dd-share-drawer', 'data-testid': 'dd-share-drawer'}}>
        <div className="drawer-inner">
          <h3>Share sight with a team</h3>
          <p className="drawer-sub">
            Grants visibility only — never stewardship or ownership — and must have an end date.
          </p>

          <Autocomplete
            options={targets}
            // The affordance is "share with a TEAM", and `listShareTargets` returns users and groups
            // in one list — so a row that does not say which it is makes the drawer's own subject
            // ambiguous. The server sends `type` for exactly this; dropping it would also leave
            // rule 5's "a team you belong to" refusal unexplainable on screen.
            getOptionLabel={(t) => (t.type === 'group' ? `${t.displayName} (team)` : t.displayName || '')}
            value={subject}
            onChange={(e, v) => this.setState({subject: v})}
            renderInput={(params) => (
              <TextField {...params} label="Share with" variant="standard"
                inputProps={{...params.inputProps, 'data-testid': 'dd-share-target'}} />
            )}
          />

          <TextField
            className="drawer-field"
            label="Until"
            type="date"
            required
            value={expiresAt}
            onChange={(e) => this.setState({expiresAt: e.target.value})}
            InputLabelProps={{shrink: true}}
            inputProps={{'data-testid': 'dd-share-expiry'}}
          />

          {error && (
            <div data-testid="dd-share-refusal">
              <AccessRefusal error={error} />
            </div>
          )}

          <div className="drawer-actions">
            <Button label="Cancel" className="btn btn-default" onClick={onClose} data-testid="dd-share-cancel" />
            <Button label="Share" className="btn btn-action" disabled={!canSubmit}
              onClick={this.handleSubmit} data-testid="dd-share-submit" />
          </div>
        </div>
      </Drawer>
    );
  }
}

export default ShareSightDrawer;
